import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Navigation from "./Components/Navigation";
import MobileNav from "./Components/MobileNav";
import Footer from "./Components/Footer";
import QuestionnaireModal from "./Components/Welcome/QuestionnaireModal";

const Layout = () => {
    // Default to dark mode for premium AI workspace look
    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem("theme");
        return saved || "dark";
    });

    const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.add("theme-transitioning");
        if (theme === "dark") {
            root.classList.add("dark");
        } else {
            root.classList.remove("dark");
        }
        localStorage.setItem("theme", theme);
        const timer = setTimeout(() => {
            root.classList.remove("theme-transitioning");
        }, 300);
        return () => clearTimeout(timer);
    }, [theme]);

    useEffect(() => {
        const handleOpen = (e) => {
            setSelectedPlan(e.detail?.plan || "");
            setIsQuestionnaireOpen(true);
        };
        window.addEventListener("open-questionnaire", handleOpen);
        return () => window.removeEventListener("open-questionnaire", handleOpen);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => (prev === "dark" ? "light" : "dark"));
    };

    return (
        <div className="flex flex-col min-h-screen dark:bg-[#191a1a] bg-[#fafafa]">
            {/* Top Announcement Bar for S&C Flagship Product */}
            <div className="w-full bg-[#007D54] text-white py-2.5 px-4 text-center text-xs font-medium tracking-wide flex items-center justify-center space-x-2 border-b border-emerald-600/30">
                <span className="bg-emerald-300 w-2 h-2 rounded-full animate-pulse"></span>
                <span>Introducing Jibu Labs' Flagship Product: <strong>Schools &amp; Careers (S&amp;C)</strong>. Explore it now at</span>
                <a href="https://schoolsandcareers.com" target="_blank" rel="noopener noreferrer" className="underline font-bold hover:text-emerald-100 transition-colors ml-1 inline-flex items-center space-x-0.5">
                    <span>schoolsandcareers.com</span>
                    <svg className="w-3.5 h-3.5 inline-block ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                </a>
            </div>

            {/* Sticky Top Header */}
            <header className="sticky top-0 z-40 w-full border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#202222] transition-colors duration-300 shadow-sm">
                {/* Desktop Top Navbar */}
                <div className="hidden lg:block">
                    <Navigation theme={theme} toggleTheme={toggleTheme} />
                </div>

                {/* Mobile Header Bar */}
                <div className="lg:hidden flex items-center justify-between px-6 py-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent logo-font">Jibu Labs</span>
                    </div>
                    <div className="flex items-center space-x-4">
                        {/* Theme toggle for mobile */}
                        <button 
                            onClick={toggleTheme} 
                            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-355 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                            aria-label="Toggle theme"
                        >
                            {theme === "dark" ? (
                                <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728L19 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5 text-forest" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            )}
                        </button>
                        {/* Hamburger menu toggle */}
                        <button 
                            onClick={() => setIsMobileMenuOpen(prev => !prev)}
                            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-350 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                            aria-label="Toggle mobile navigation"
                            aria-expanded={isMobileMenuOpen}
                            aria-controls="mobile-nav-drawer"
                            aria-haspopup="true"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-grow min-h-screen flex flex-col justify-between overflow-x-hidden">
                <div className="flex-grow">
                    <Outlet />
                </div>
                <Footer />
            </main>

            {/* Mobile Nav Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <MobileNav 
                        theme={theme} 
                        toggleTheme={toggleTheme} 
                        isOpen={isMobileMenuOpen} 
                        onClose={() => setIsMobileMenuOpen(false)} 
                    />
                )}
            </AnimatePresence>

            {/* Questionnaire Modal */}
            <QuestionnaireModal 
                isOpen={isQuestionnaireOpen} 
                onClose={() => setIsQuestionnaireOpen(false)} 
                selectedPlan={selectedPlan} 
            />
        </div>
    );
};

export default Layout;